import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import {
  addEligibleProject,
  VotingPeriod,
} from "utils/apiService/PinkSale";

interface AddProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  campaign: VotingPeriod | null;
  onSuccess?: () => void;
}

interface AddProjectFormData {
  launchpadId: string;
}

export default function AddProjectModal({
  isOpen,
  onClose,
  campaign,
  onSuccess,
}: AddProjectModalProps) {
  const toast = useToast();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<AddProjectFormData>({
    defaultValues: {
      launchpadId: "",
    },
  });

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      reset({
        launchpadId: "",
      });
    }
  }, [isOpen, reset]);

  const onSubmit = async (data: AddProjectFormData) => {
    if (!campaign) return;

    try {
      await addEligibleProject(campaign._id, {
        launchpadId: data.launchpadId.trim(),
      });

      toast({
        title: "Success",
        description: "Project added successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      onSuccess?.();
      onClose();
    } catch (error: any) {
      toast({
        title: "Error",
        description:
          error?.message || "Failed to add project",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleClose = () => {
    reset({
      launchpadId: "",
    });
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Add eligible project</ModalHeader>
        <ModalCloseButton />
        <form onSubmit={handleSubmit(onSubmit)}>
          <ModalBody pb={6}>
            <VStack spacing={4} align="stretch">
              <FormControl isInvalid={Boolean(errors.launchpadId)} isRequired>
                <FormLabel>Launchpad ID</FormLabel>
                <Input
                  placeholder="Enter launchpad ID (MongoDB ObjectId)"
                  {...register("launchpadId", {
                    required: "Launchpad ID is required",
                    validate: (value) => {
                      const trimmed = value.trim();
                      if (!trimmed) {
                        return "Launchpad ID cannot be empty";
                      }
                      // Basic MongoDB ObjectId validation (24 hex characters)
                      if (!/^[a-fA-F0-9]{24}$/.test(trimmed)) {
                        return "Invalid MongoDB ObjectId format";
                      }
                      return true;
                    },
                  })}
                />
                <FormErrorMessage>
                  {errors.launchpadId?.message}
                </FormErrorMessage>
              </FormControl>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button
              colorScheme="blue"
              mr={3}
              type="submit"
              isLoading={isSubmitting}
            >
              Add Project
            </Button>
            <Button onClick={handleClose} isDisabled={isSubmitting}>
              Cancel
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
}

